import { Map } from 'immutable';

import { shapeAltTextForAPI, shapeCategoriesForAPI, shapeDescriptionForAPI, shapeEbayCategoriesForAPI, shapeEbayItemSpecificsForAPI, shapeFaireVariationsForAPI, shapeKeywordsForAPI, shapeKeywordsForApp, shapeTagsForAPI } from '@/utils/product/shapeAttributes';
import { filterNewTags, tagsFromArray } from '@/utils/tags';
import { getTagError } from '@/utils/validations/tags';
import { getSize } from '@/utils/iterable/getSize';
import { keepOld } from '@/utils/iterable/keepOld';
import { convert } from '@/utils/product/convert';

import { AI_OPERATION, AI_OPERATION_LABEL, AI_OPERATIONS, ATTRIBUTE_TO_DESTINATION } from '@/constants/ai';
import { MAX_NUMBER_OF_AI_TAGS, MAX_NUMBER_OF_TAGS } from '@/constants/validations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ETSY_AI_CHANNELS } from '@/constants/bulkEdit';
import { AI, NEW, VALUE } from '@/constants/product';
import { ATTRIBUTE } from '@/constants/attributes';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';

export function shapeAIResponseForApp({ ai, attribute, channel, response }) {
  function filterInvalidTags(tag) {
    return !getTagError({ channel, tag });
  }

  switch (attribute) {
    case ATTRIBUTE.DESCRIPTION: {
      return response.text
        ? convert({ from: ETSY, to: channel, type: attribute, value: response.text })
        : '';
    }

    case ATTRIBUTE.TAGS: {
      if (!Array.isArray(response.text) || getSize(response.text) === 0) return EMPTY.ORDERED_MAP;

      switch (ai.get('operation')) {
        case AI_OPERATION.ADD_MAXIMUM: {
          return tagsFromArray(response.text.filter(filterInvalidTags))
            .filter(filterNewTags(ai.get('original')))
            .slice(0, MAX_NUMBER_OF_TAGS[channel]);
        }

        default: {
          return tagsFromArray(response.text.filter(filterInvalidTags).slice(0, MAX_NUMBER_OF_TAGS[channel]));
        }
      }
    }

    case ATTRIBUTE.KEYWORDS: {
      return shapeKeywordsForApp({ channel, value: response.text });
    }

    default: {
      return response.text || '';
    }
  }
}

export function getAIOperations({ attribute, channel, product }) {
  function mapDescriptionOperations(operation) {
    return {
      disabled: !product.getIn([FORM.TITLE, VALUE]),
      label: AI_OPERATION_LABEL[operation],
      type: operation,
    };
  }

  function mapSeoOperations(operation) {
    return {
      disabled: !product.getIn([FORM.TITLE, VALUE]),
      label: AI_OPERATION_LABEL[operation],
      type: operation,
    };
  }

  function mapTagsOperations(operation) {
    switch (operation) {
      case AI_OPERATION.ADD_MAXIMUM: {
        return {
          disabled: (
            !product.getIn([FORM.TITLE, VALUE]) || (
              MAX_NUMBER_OF_AI_TAGS.hasOwnProperty(channel) &&
              getSize(product.getIn([FORM.TAGS, VALUE]).mergeWith(keepOld, product.getIn([FORM.TAGS, NEW]))) >=
              MAX_NUMBER_OF_AI_TAGS[channel]
            )
          ),
          label: AI_OPERATION_LABEL[operation],
          type: operation,
        };
      }

      default: {
        return {
          disabled: !product.getIn([FORM.TITLE, VALUE]),
          label: AI_OPERATION_LABEL[operation],
          type: operation,
        };
      }
    }
  }

  function mapTitleOperations(operation) {
    return {
      disabled: !product.getIn([FORM.TITLE, VALUE]),
      label: AI_OPERATION_LABEL[operation],
      type: operation,
    };
  }

  function mapOperations(operation) {
    return {
      label: AI_OPERATION_LABEL[operation],
      type: operation,
    };
  }

  switch (channel) {
    case EBAY: {
      switch (attribute) {
        case ATTRIBUTE.DESCRIPTION: {
          return AI_OPERATIONS[channel][ATTRIBUTE.DESCRIPTION].map(product ? mapDescriptionOperations : mapOperations);
        }

        case ATTRIBUTE.TITLE: {
          return AI_OPERATIONS[channel][ATTRIBUTE.TITLE].map(product ? mapTitleOperations : mapOperations);
        }

        default: {
          return [];
        }
      }
    }

    case ETSY: {
      switch (attribute) {
        case ATTRIBUTE.DESCRIPTION: {
          return AI_OPERATIONS[channel][ATTRIBUTE.DESCRIPTION].map(product ? mapDescriptionOperations : mapOperations);
        }

        case ATTRIBUTE.TAGS: {
          return AI_OPERATIONS[channel][ATTRIBUTE.TAGS].map(product ? mapTagsOperations : mapOperations);
        }

        case ATTRIBUTE.TITLE: {
          return AI_OPERATIONS[channel][ATTRIBUTE.TITLE].map(product ? mapTitleOperations : mapOperations);
        }

        default: {
          return [];
        }
      }
    }

    case FAIRE: {
      switch (attribute) {
        case ATTRIBUTE.DESCRIPTION: {
          return AI_OPERATIONS[channel][ATTRIBUTE.DESCRIPTION].map(product ? mapDescriptionOperations : mapOperations);
        }

        case ATTRIBUTE.TITLE: {
          return AI_OPERATIONS[channel][ATTRIBUTE.TITLE].map(product ? mapTitleOperations : mapOperations);
        }

        default: {
          return [];
        }
      }
    }

    case SHOPIFY: {
      switch (attribute) {
        case ATTRIBUTE.DESCRIPTION: {
          return AI_OPERATIONS[channel][ATTRIBUTE.DESCRIPTION].map(product ? mapDescriptionOperations : mapOperations);
        }

        case ATTRIBUTE.META_DESCRIPTION: {
          return AI_OPERATIONS[channel][ATTRIBUTE.META_DESCRIPTION].map(product ? mapSeoOperations : mapOperations);
        }

        case ATTRIBUTE.PAGE_TITLE: {
          return AI_OPERATIONS[channel][ATTRIBUTE.PAGE_TITLE].map(product ? mapSeoOperations : mapOperations);
        }

        case ATTRIBUTE.SEO: {
          return AI_OPERATIONS[channel][ATTRIBUTE.SEO].map(product ? mapSeoOperations : mapOperations);
        }

        case ATTRIBUTE.TITLE: {
          return AI_OPERATIONS[channel][ATTRIBUTE.TITLE].map(product ? mapTitleOperations : mapOperations);
        }

        default: {
          return [];
        }
      }
    }

    default: {
      return [];
    }
  }
}

export function getAIRequest({ attribute, product, shop }) {
  const channel = product.get('channel');
  const key = product.get('productId');
  const shopId = product.get('shopId');
  const db = shop.get('db');
  const destination = ATTRIBUTE_TO_DESTINATION[attribute] || attribute;

  switch (attribute) {
    case ATTRIBUTE.DESCRIPTION: {
      let state = product.getIn([FORM.DESCRIPTION, AI]);
      const description = product.getIn([FORM.DESCRIPTION, VALUE]);
      const title = product.getIn([FORM.TITLE, VALUE]);

      if (!state.has('original')) {
        state = state.set('original', description);
      }

      const request = {
        attribute,
        channel,
        destination,
        input: {
          description,
          title,
          instructions: state.get('instructions'),
        },
        key,
        operation: state.get('operation'),
        shopId,
        db,
      };

      switch (channel) {
        case ETSY: {
          request.input.tags = shapeTagsForAPI({ channel, product });
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.image_alt_text = shapeAltTextForAPI({ channel, product });
          break;
        }

        case SHOPIFY: {
          const keywords = shapeKeywordsForAPI({ channel, value: product.get(ATTRIBUTE.KEYWORDS) });
          request.input.all_keywords = keywords.all_keywords;
          request.input.focus_keyword = keywords.focus_keyword;
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.image_alt_text = shapeAltTextForAPI({ channel, product });
          request.input.seo_title = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE]);
          request.input.seo_description = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE]);
          request.input.shop_name = shop.get('name');
          request.input.tags = shapeTagsForAPI({ channel, product });
          request.input.vendor = product.getIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE]);
          break;
        }

        case EBAY: {
          const keywords = shapeKeywordsForAPI({ channel, value: product.get(ATTRIBUTE.KEYWORDS) });
          request.input.all_keywords = keywords.all_keywords;
          request.input.focus_keyword = keywords.focus_keyword;
          request.input.item_specifics = shapeEbayItemSpecificsForAPI({ product });
          request.input.category = shapeEbayCategoriesForAPI({ product, shop });
          break;
        }

        case FAIRE: {
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.shop_name = shop.get('name');
          request.input.variant_options = shapeFaireVariationsForAPI({ product });
          break;
        }

        default: {
          break;
        }
      }

      return { request, state };
    }

    case ATTRIBUTE.KEYWORDS: {
      let state = product.getIn([ATTRIBUTE.KEYWORDS, AI], EMPTY.MAP);

      if (!state.has('original')) {
        const allKeywords = product.getIn([ATTRIBUTE.KEYWORDS, 'all']);
        const focusKeyword = product.getIn([ATTRIBUTE.KEYWORDS, 'focus']);
        state = state.set('original', Map({ all: allKeywords, focus: focusKeyword }));
      }

      const request = {
        attribute,
        channel,
        destination,
        input: {
          title: product.getIn([FORM.TITLE, VALUE]),
          description: shapeDescriptionForAPI({ channel, product }),
        },
        key,
        operation: AI_OPERATION.GENERATE,
        shopId,
        db,
      };

      switch (channel) {
        case EBAY: {
          request.input.item_specifics = shapeEbayItemSpecificsForAPI({ product });
          request.input.category = shapeEbayCategoriesForAPI({ product, shop });
          break;
        }

        case SHOPIFY: {
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.image_alt_text = shapeAltTextForAPI({ channel, product });
          request.input.seo_description = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE]);
          request.input.seo_title = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE]);
          request.input.shop_name = shop.get('name');
          request.input.tags = shapeTagsForAPI({ channel, product });
          request.input.vendor = product.getIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE]);
          break;
        }
        default: {
          break;
        }
      }

      return { request, state };
    }

    case ATTRIBUTE.META_DESCRIPTION:
    case ATTRIBUTE.PAGE_TITLE: {
      let state = product.getIn([FORM.SEO, AI, attribute]);
      const shopName = shop.get('name');
      const description = shapeDescriptionForAPI({ channel, product });
      const title = product.getIn([FORM.TITLE, VALUE]);
      const pageTitle = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE]);
      const metaDescription = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE]);
      const category = shapeCategoriesForAPI({ channel, product });
      const altText = shapeAltTextForAPI({ channel, product });
      const keywords = shapeKeywordsForAPI({ channel, value: product.get(ATTRIBUTE.KEYWORDS) });
      const tags = shapeTagsForAPI({ channel, product });
      const vendor = product.getIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE]);
      const request = {
        attribute,
        channel,
        destination,
        input: {
          all_keywords: keywords.all_keywords,
          category,
          description,
          focus_keyword: keywords.focus_keyword,
          image_alt_text: altText,
          instructions: state.get('instructions'),
          seo_title: pageTitle,
          seo_description: metaDescription,
          shop_name: shopName,
          tags,
          title,
          vendor,
        },
        key,
        operation: state.get('operation'),
        shopId,
        db,
      };

      if (!state.has('original')) {
        state = state.set('original', request.input[destination]);
      }

      return { request, state };
    }

    case ATTRIBUTE.TAGS: {
      let state = product.getIn([FORM.TAGS, AI]);
      const description = shapeDescriptionForAPI({ channel, product });
      const title = product.getIn([FORM.TITLE, VALUE]);

      if (!state.has('original')) {
        const tags = product
          .getIn([FORM.TAGS, VALUE])
          .mergeWith(keepOld, product.getIn([FORM.TAGS, NEW]));

        state = state.set('original', tags);
      }

      const request = {
        attribute,
        channel,
        destination,
        input: {
          description,
          title,
          tags: shapeTagsForAPI({ channel, product }),
          instructions: state.get('instructions'),
        },
        key,
        operation: state.get('operation'),
        shopId,
        db,
      };

      return { request, state };
    }

    case ATTRIBUTE.TITLE: {
      let state = product.getIn([FORM.TITLE, AI]);
      const description = shapeDescriptionForAPI({ channel, product });
      const title = product.getIn([FORM.TITLE, VALUE]);

      if (!state.has('original')) {
        state = state.set('original', title);
      }

      const request = {
        attribute,
        channel,
        destination,
        input: {
          description,
          title,
          instructions: state.get('instructions'),
        },
        key,
        operation: state.get('operation'),
        shopId,
        db,
      };

      switch (channel) {
        case ETSY: {
          request.input.tags = shapeTagsForAPI({ channel, product });
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.image_alt_text = shapeAltTextForAPI({ channel, product });

          const titleFormatPreference = shop.get('titleFormatPreference');
          request.channel = ETSY;

          if (titleFormatPreference) {
            request.channel = ETSY_AI_CHANNELS[titleFormatPreference];
          }

          break;
        }

        case SHOPIFY: {
          const keywords = shapeKeywordsForAPI({ value: product.get(ATTRIBUTE.KEYWORDS) });
          request.input.all_keywords = keywords.all_keywords;
          request.input.focus_keyword = keywords.focus_keyword;
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.image_alt_text = shapeAltTextForAPI({ channel, product });
          request.input.seo_title = product.getIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, VALUE]);
          request.input.seo_description = product.getIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, VALUE]);
          request.input.shop_name = shop.get('name');
          request.input.tags = shapeTagsForAPI({ channel, product });
          request.input.vendor = product.getIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE]);
          break;
        }

        case EBAY: {
          request.input.item_specifics = shapeEbayItemSpecificsForAPI({ product });
          request.input.category = shapeEbayCategoriesForAPI({ product, shop });
          const keywords = shapeKeywordsForAPI({ value: product.get(ATTRIBUTE.KEYWORDS) });
          request.input.all_keywords = keywords.all_keywords;
          request.input.focus_keyword = keywords.focus_keyword;
          break;
        }

        case FAIRE: {
          request.input.category = shapeCategoriesForAPI({ channel, product });
          request.input.shop_name = shop.get('name');
          break;
        }

        default: {
          break;
        }
      }

      return { request, state };
    }

    default: {
      return null;
    }
  }
}
